import { size } from '@floating-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce, noop } from 'lodash';
import { useDropdownBase } from '../../DropdownBase';
import { useDropdownSearch } from './useDropdownSearch';
function useDropdown({
  variant,
  placement,
  query = '',
  value,
  multiple,
  open,
  disabled,
  onSubmit,
  onChange,
  onSearch = noop,
  options,
  withSearchHighlight,
  searchDebounceMs,
  clearSearch
}) {
  const [isLoading, setLoading] = useState(false);
  const [showOptions, setShowOptions] = useState(open != null ? open : false);
  const [selectedValue, setSelectedValue] = useState(value || (multiple ? [] : {}));
  const {
    searchInputValue,
    searchQuery,
    resetSearch,
    handleSearch
  } = useDropdownSearch({
    query,
    onSearch,
    searchDebounceMs
  });
  // @CRUTCH: This debounced fn is introduced to work around the issue of
  // onSearch being called twice with an empty string when tryting to reset search when dropdown closes.
  // When dropdown is closed by clicking outside of reference element
  // floating-ui treats it as two "close" events: one for outside click, the other for reference element
  // losing focus. Please don't remove until better fix is available.
  const onSearchDebounced = useMemo(() => debounce(onSearch, 100), [onSearch]);
  const [showAllSelectedOptions, setShowAllSelectedOptions] = useState(false);
  useEffect(() => {
    setSelectedValue(value || (multiple ? [] : {}));
  }, [value]);
  const closeOptions = useCallback(() => {
    setShowAllSelectedOptions(false);
    setShowOptions(false);
  }, []);
  const openOptions = useCallback(() => {
    setShowOptions(true);
  }, [disabled]);
  function useFloatingProps(ref) {
    const {
      referenceProps,
      floatingProps
    } = useDropdownBase({
      ref,
      disabled,
      placement,
      open: showOptions,
      onChange: nextOpen => {
        setShowOptions(nextOpen);
        if (!nextOpen) {
          resetSearch();
          onSearchDebounced('');
        }
      },
      middlewares: variant === 'input' ? [size({
        apply: ({
          rects,
          elements
        }) => {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`
          });
        }
      })] : []
    });
    return {
      referenceProps,
      floatingProps
    };
  }
  const memoizedOptions = useMemo(() => {
    if (searchQuery.length > 0 && !withSearchHighlight) {
      return options == null ? void 0 : options.filter(option => option.title.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    return options;
  }, [searchQuery, options, withSearchHighlight]);
  function isMultipleOptions(selectedValues) {
    return Array.isArray(selectedValues);
  }
  const handleCancel = useCallback(() => {
    const nextValue = multiple ? [] : {};
    closeOptions();
    setSelectedValue(nextValue);
    onChange == null ? void 0 : onChange(nextValue);
    resetSearch();
  }, [multiple, closeOptions, onChange, resetSearch]);
  const handleApply = useCallback(async () => {
    setLoading(true);
    await (onSubmit == null ? void 0 : onSubmit());
    setLoading(false);
    closeOptions();
  }, [onSubmit, closeOptions]);
  const handleShowAllSelectedOptions = useCallback(() => {
    closeOptions();
    setShowAllSelectedOptions(true);
  }, [closeOptions]);
  const toggleShowOptions = useCallback(() => {
    setShowOptions(!showOptions);
  }, [showOptions]);
  useEffect(() => {
    if (!showOptions && clearSearch) {
      resetSearch();
    }
  }, [showOptions, clearSearch, resetSearch]);
  const handleRemoveOption = useCallback(option => {
    setSelectedValue(previousState => {
      if (isMultipleOptions(previousState)) {
        const nextState = previousState.filter(previousOption => previousOption.id !== option.id);
        if (previousState.length === 4) {
          setShowAllSelectedOptions(false);
        }
        onChange == null ? void 0 : onChange(nextState);
        return nextState;
      }
      return previousState;
    });
  }, []);
  const handleClickItem = useCallback(option => {
    if (isMultipleOptions(selectedValue)) {
      const nextValue = [...selectedValue];
      const optionIndex = nextValue.findIndex(({
        id
      }) => id === option.id);
      if (optionIndex > -1) {
        nextValue.splice(optionIndex, 1);
      } else {
        nextValue.push(option);
      }
      onChange == null ? void 0 : onChange(nextValue);
      setSelectedValue(nextValue);
    } else {
      onChange == null ? void 0 : onChange(option);
      setSelectedValue(option);
      setShowOptions(false);
    }
  }, [selectedValue, onChange]);
  const handleSelectAll = useCallback(() => {
    const nextValue = isMultipleOptions(selectedValue) && selectedValue.length === options.length ? [] : memoizedOptions;
    setSelectedValue(nextValue);
    onChange == null ? void 0 : onChange(nextValue);
  }, [options, memoizedOptions, selectedValue, onChange]);
  const isSelected = useCallback(option => {
    if (isMultipleOptions(selectedValue)) {
      var _selectedValue$filter;
      return ((_selectedValue$filter = selectedValue.filter(currentValue => currentValue.id === option.id)) == null ? void 0 : _selectedValue$filter.length) > 0;
    }
    if (!selectedValue) {
      return false;
    }
    return selectedValue.id === option.id;
  }, [selectedValue]);
  return {
    multipleSelectedValues: multiple ? selectedValue : [],
    selectedValue: !multiple ? selectedValue : {},
    searchValue: searchInputValue,
    showOptions,
    showAllSelectedOptions,
    options: memoizedOptions,
    isLoading,
    toggleShowOptions,
    handleSearch,
    isSelected,
    handleRemoveOption,
    handleSelectAll,
    handleClickItem,
    handleCancel,
    handleApply,
    useFloatingProps,
    handleShowAllSelectedOptions,
    openOptions,
    closeOptions,
    setSelectedValue
  };
}
export default useDropdown;